// Landing page copy — English (reference language).
//
// Refonte « jour → nuit » du 9 octobre 2026. Toute la page raconte UNE idée :
// « toi ce matin » pose le verrou, « toi à 2 h 47 » le trouve fermé. Le
// défilement va du blanc du matin au bleu nuit, et revient au matin à la fin.
//
// Aucune preuve inventée sur cette page (ni avis, ni note, ni spécialiste) :
// chaque phrase est une affirmation qu'on peut tenir.
//
// Les clés marquées « partagées » sont lues ailleurs que sur la landing
// (espace client, pages légales, SEO) : ne pas les renommer.
const landing = {
  nav: {
    links: [
      { href: '#night', label: 'Why it fails' },
      { href: '#how', label: 'How it works' },
      { href: '#pricing', label: 'Pricing' },
      { href: '#faq', label: 'FAQ' }
    ],
    login: 'Log in',                 // partagée
    start: 'Get started',            // partagée — UN SEUL libellé partout
    home: 'SELFCNTRL — home',        // partagée
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    language: 'Language',            // partagée
    switchTo: 'Switch to English'
  },

  hero: {
    clock: '02:47',
    clockL: 'Tonight · access blocked',
    // Le titre est découpé pour poser le trait de marqueur sur « can't ».
    t1: 'The porn blocker',
    t2: 'you',
    t3: 'can’t',
    t4: 'uninstall at 2am.',
    lead: `Lock every device you own once, on a clear morning. The filter holds
           at night — when you’re tired, alone and ready to give in — because
           the button to turn it off simply isn’t there.`,
    cta: 'Get started',
    ctaAlt: 'See pricing',
    meta: ['$9.99/month', '30-day money-back', 'Nothing read, nothing stored'],
    works: 'Works on',
    // Le film.
    watch: 'Play the 30-second film',
    watchShort: 'Watch · 30 s',
    filmTitle: 'selfcntrl — the short version',
    filmCap: 'Thirty seconds, sound optional. What happens when you try to get around it.'
  },

  // Le téléphone verrouillé (PhoneMock) — aussi utilisé par les pages
  // d'auth et l'onboarding. Libellés identiques au vrai écran de blocage.
  mock: {
    brand: 'Selfcntrl',
    nav: ['Overview', 'My lock', 'Urge SOS', 'Goals', 'Rewards'],
    days: 21,
    daysL: 'days held',
    since: 'Since September 18',
    lockT: 'Lock active',
    lockS: '3 devices locked. There is no open door left.',
    devices: [
      { n: 'iPhone 15', s: 'Supervised · locked' },
      { n: 'MacBook Air', s: 'Locked' },
      { n: 'Windows PC', s: 'Locked' }
    ],
    blockedN: '1,284',
    blockedL: 'requests blocked · 14 days',
    phoneK: 'Selfcntrl',
    phoneT: <>Access<br />blocked</>,
    phoneS: 'Category locked. Nothing to negotiate before morning.',
    phoneDay: 'Day 21'
  },

  // La frise de la journée : le cœur de l'identité.
  day: {
    kicker: 'One lock, two of you',
    // Le titre est découpé : le soleil et la lune en trame de points
    // (public/media/dot-sun.webp, dot-moon.webp) s'insèrent entre les mots.
    t1: 'The you of this morning',
    t2: 'decides for the you of tonight.',
    pins: [
      { time: '09:12', who: 'You, clear-headed', t: 'You set the lock', p: 'Ten minutes, once. Every device you own.' },
      { time: '23:00', who: 'Curfew', t: 'The internet goes quiet', p: 'Optional. You pick the hours you usually slip.' },
      { time: '02:47', who: 'You, at your weakest', t: 'The door is already shut', p: 'Nothing to uninstall, nothing to negotiate.' },
      { time: '07:30', who: 'Day 22', t: 'You wake up without it', p: 'The counter climbs. The shame doesn’t.' }
    ]
  },

  // Quatre faits, aucun inventé.
  facts: [
    { t: '10 min', s: 'to lock a device, once' },
    { t: '24 h', s: 'before any loosening takes effect' },
    { t: '$100', s: 'paid to you if you manage to remove it' },
    { t: '0', s: 'browsing history read or stored' }
  ],

  // La nuit : ce qui a déjà raté.
  problem: {
    kicker: 'Why it keeps coming back',
    title: <>At 2:47am you are<br />not the same person.</>,
    lead: `You have stopped before. Three days, maybe two weeks. Then one tired
           evening it all started again. It was never your character — every
           tool you tried could be undone by the person you are at night.`,
    triedK: 'What you’ve already tried',
    tried: [
      { t: 'Screen Time', why: 'You set the passcode. You know the passcode.' },
      { t: 'A free blocker app', why: 'Uninstalled in thirty seconds, then reinstalled in the morning.' },
      { t: 'Private mode, a VPN', why: 'The filter watched one browser. You found the other door.' },
      { t: '“That was the last time.”', why: 'Willpower is full at 9am and empty at 2am. The urge isn’t.' }
    ],
    turn1: 'Don’t fight the urge every night.',
    turn2: 'Remove the option.',
    afterK: 'The morning after',
    afterTime: '07:30',
    afterChip: 'Day 22 · nothing happened',
    afterP: `Phone face down, head clear. Not because you won a fight at 2am —
             because there was no fight to have. That is what you are buying.`
  },

  how: {
    kicker: 'How it works',
    title: 'Three moves. Then nothing left to decide.',
    lead: 'Set it up on a day you’re doing fine. It does the work on the days you’re not.',
    steps: [
      {
        time: '2 min',
        t: 'Create your space',
        p: `One email, one password. Nobody else is told — no accountability
            partner, no report sent to anyone.`,
        vis: 'account'
      },
      {
        time: '10 min / device',
        t: 'Lock your devices',
        p: `iPhone and iPad are supervised: the filter profile can’t be
            removed from the phone. Mac and Windows run a small app you can’t
            quit.`,
        vis: 'lock'
      },
      {
        time: 'Every day after',
        t: 'Live your life',
        p: `Every app, every browser, private mode included. Your day counter
            climbs. The nightly negotiation is over.`,
        vis: 'live'
      }
    ],
    vis: {
      email: 'you@example.com',
      pass: '••••••••••',
      btn: 'Create my space',
      locked: 'Locked',
      profile: 'Filter profile · can’t be removed',
      day: n => `Day ${n}`,
      streak: 'Current streak'
    }
  },

  // Le téléphone annoté : les six mécanismes, en schéma.
  inside: {
    kicker: 'Under the hood',
    title: 'Built for the moment you want it off.',
    lead: 'Every mechanism exists for one reason: a weak moment must not be enough.',
    items: [
      { k: 'supervised', t: 'Supervised iPhone & iPad', p: 'The profile isn’t deletable from Settings. Only from your account, after a cooling-off delay.' },
      { k: 'dns', t: 'Every app, every browser', p: 'Blocked at DNS level, below the browser. Safari, Chrome, Reddit, X: same lock.' },
      { k: 'vpn', t: 'VPN & incognito-proof', p: 'A workaround VPN can’t be added on a supervised phone. Private tabs change nothing.' },
      { k: 'ratchet', t: 'Tighten now, loosen in 24 h', p: 'Adding a locked day is instant. Removing one waits a full night.' },
      { k: 'night', t: 'Night curfews', p: 'Cut the internet entirely during the hours you slip — per weekday, to the minute.' },
      { k: 'more', t: 'Block more if you need to', p: 'Dating, gambling, social media, specific sites. One more switch, same 24 h rule.' }
    ]
  },

  // Partagée (ancienne section, gardée pour compatibilité).
  features: { items: [] },

  // LE COMPARATIF.
  rivals: {
    kicker: 'Compared',
    title: 'Every box the others leave empty.',
    lead: `Screen Time and the classic blocker apps each tick a few of these.
           None ticks all of them.`,
    colOthers: 'Others',
    colUs: 'SELFCNTRL',
    rows: [
      { k: 'Every device you own', others: false, us: true },
      { k: 'Can’t be turned off on impulse', others: false, us: true },
      { k: 'Covers private browsing', others: false, us: true },
      { k: 'VPN-proof', others: false, us: true },
      { k: 'Night curfews', others: true, us: true },
      { k: 'Cashback while you hold', others: false, us: true },
      { k: '$100 if you beat it', others: false, us: true },
      { k: 'No activity tracking', others: false, us: true }
    ],
    note: `“Others” means Screen Time and the paid blocker apps. Each covers part
           of this list — none covers all of it, and none holds the code for you.`
  },

  // Partagée : t.weaning.days / daysFull sont lues par l'espace client.
  weaning: {
    days: ['M', 'T', 'W', 'T', 'F', 'S', 'S'],
    daysFull: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
  },

  bet: {
    amount: '$100',
    stamp: 'IF YOU BEAT IT · WE PAY YOU · SELFCNTRL · ',
    kicker: 'The bet',
    title: 'Get around it, and we pay you.',
    body: `Every blocker claims to be unbeatable. We put money on it: strip the
           filter off a device we locked, by your own means, and we send you a
           hundred dollars on top of a full refund.`,
    note: 'One claim per person · device supervised by SELFCNTRL',
    cta: 'Get started'
  },

  // ==========================================================================
  //  REWARDS — l'équilibrage vit ICI (lu par le composant Rewards).
  // ==========================================================================
  rewards: {
    plan: {
      parMois: 200,
      bonus: { 6: 500, 12: 1000 },
      parCarte: 1000,
      valeurCarte: 5,
      mois: 12
    },
    kicker: 'Cashback',
    title: 'Every month you hold pays you back.',
    lead: `200 CNTRL land on your account every month the lock stays on, with
           bonuses at six months and one year. 1,000 CNTRL buy a $5 gift card.`,
    rowMonth: 'Every month it holds',
    rowSix: 'Bonus at six months',
    rowYear: 'Bonus at one year',
    rowTotal: 'Your first year',
    perMonth: '/ mo',
    cardK: 'Gift card',
    cardBrands: 'Amazon · Airbnb · Starbucks · Google Play · App Store',
    cta: 'Get started',
    ctaNote: 'The counter starts on your first paid month.',
    fine: `Paying members only, monthly or yearly. CNTRL run while the account
           is active and the lock is in place; they are not redeemable for cash
           and not transferable.`
  },

  privacy: {
    kicker: 'Privacy',
    title: 'It locks. It doesn’t look.',
    lead: `You are handing a lock to a company. Here is exactly how far it
           reaches — and where it stops.`,
    items: [
      { t: 'Not your photos, messages or passwords', p: 'Nothing on the device is read. The lock restricts, it does not look.' },
      { t: 'Not a company phone', p: 'No remote commands, no app installed or removed for you, no wipe, no location.' },
      { t: 'No history kept', p: 'Filtering works on whole categories. Nothing about your browsing is stored.' },
      { t: 'Nobody is told', p: 'No accountability partner, no email to a third party, no shared dashboard.' },
      { t: 'You get your phone back', p: 'Abandoning the profile releases the device once the cooling-off delay has run.' },
      { t: 'Discreet billing', p: 'Nothing explicit appears on your bank statement.' }
    ],
    note: 'Written in plain terms in the privacy policy, with the processors we actually use.'
  },

  // La démo animée du blocage (components/landing/TryIt.js). Le délai de
  // suppression = REMOVAL_DELAY_HOURS côté back (72 h).
  tryIt: {
    kicker: 'See it happen',
    title: '2:47am. He types it. Nothing opens.',
    lead: 'Any browser, any app, private mode included. And when he goes to delete the filter, there is nothing to delete.',
    steps: ['He searches for it', 'SELFCNTRL blocks it', 'He tries to remove the filter'],
    query: 'porxxxhx.com',
    sugg: ['porxxxhx.com', 'pxrn videos', 'free pxrn'],
    blockT: 'Blocked.',
    blockP: 'This site is filtered on this device.',
    blockBy: 'Protected by SELFCNTRL',
    setBack: 'Settings',
    setTitle: 'VPN & Device Management',
    setSub: 'Supervised · managed profile',
    setRemove: 'Remove Profile',
    alertT: 'Can’t be removed here',
    alertP: 'Removal is a request from your account — then a 72-hour wait.',
    chip1: 'Can’t be uninstalled on the spot',
    chip2: 'Removal: a request, then 72 h',
    cta: 'Lock my phone like this'
  },

  // Témoignages — mêmes textes qu'avant la refonte (écrits en interne, cf.
  // mémoire « landing-preuves-inventees »). Remis le 9 oct. 2026.
  voices: {
    kicker: 'They made it',
    title: 'They made it to the other side.',
    lead: 'Men who had all already failed several times before.',
    freeFor: s => `Free for ${s}`,
    verified: 'Verified',
    items: [
      { q: "I'd tried everything since I was nineteen. What changed here is that I simply couldn't. After two weeks my brain stopped looking.", n: 'Mehdi', a: '26', s: '8 months' },
      { q: "The night I wanted to uninstall everything, I couldn't. I was furious. The next morning I was grateful.", n: 'Lucas', a: '31', s: '1 year' },
      { q: 'Nobody knows I use it. No accountability partner, no support group. A closed door, and my life coming back.', n: 'Antoine', a: '23', s: '5 months' },
      { q: 'The hard part lasted five days. After that the urge became background noise, then nothing at all.', n: 'Yanis', a: '28', s: '4 months' },
      { q: "I wake up without shame. I hadn't realised how much it weighed every single morning.", n: 'Thomas', a: '34', s: '7 months' },
      { q: 'My girlfriend knows nothing about it, and yet she told me I was more present. That’s when it clicked.', n: 'Sofiane', a: '25', s: '3 months' },
      { q: "I'd tried three free apps. I uninstalled every one within a month. This one, I couldn't.", n: 'Kevin', a: '22', s: '6 months' },
      { q: 'The 11pm curfew changed everything. It was always at those hours that I cracked.', n: 'Raphaël', a: '29', s: '5 months' },
      { q: 'Two hours a day back. I got back in the gym and finished two books in a month.', n: 'Bilal', a: '27', s: '2 months' },
      { q: "I thought it was a willpower problem. It was just an access problem.", n: 'Grégoire', a: '36', s: '9 months' }
    ]
  },

  // Partagée : t.plans.* est lue par la page Abonnement de l'espace client.
  plans: {
    kicker: 'Pricing',
    title: 'Cheaper than what it costs you.',
    lead: `Add up the time, the sleep and the self-respect you lose every
           month, then compare. Refunded within thirty days if it isn’t for you.`,
    monthlyName: 'Monthly',
    monthlyPrice: '$9.99',
    // L'ancre « par jour » : vraie division, pas un chiffre arrondi à la baisse.
    monthlyDay: 'About 33¢ a day',
    yearlyDay: 'About 14¢ a day',
    monthlyPer: ' / month',
    yearlyName: 'Yearly',
    yearlyPrice: '$49.99',
    yearlyPer: ' / year',
    yearlyOld: '$120 on monthly',
    save: <><b>$70 saved</b> against paying monthly — about $4.17 a month.</>,
    monthly: [
      'Every device you own, locked',
      'Night curfews and extra blocking',
      'Cashback every month you hold',
      'Cancel at any time'
    ],
    yearly: [
      'Everything in the monthly plan',
      'Works out to $4.17 a month',
      'Billed once a year',
      '30-day refund, no questions asked'
    ],
    ctaMonthly: 'Start monthly',
    best: 'Best value',
    off: 'Save 58%',
    ctaYearly: 'Get the year',
    fineMonthly: '$9.99/mo — cancel anytime, 30-day money-back',
    fineYearly: '$49.99 billed once a year — 30-day money-back',
    guarT: 'Thirty days, refunded without justification.',
    guarP: `And a hundred dollars on top if you manage to strip the filter off a
            device we locked. You are not risking the price of the app — we are.`,
    note: 'Discreet payment: nothing explicit appears on your bank statement.'
  },

  // Partagée : t.faq.items alimente les données structurées FAQPage.
  faq: {
    kicker: 'FAQ',
    title: 'What you’re actually wondering.',
    help: 'Still a question?',
    helpLink: 'Ask us on WhatsApp',
    items: [
      {
        q: 'Can I uninstall it if I crack?',
        a: `No, and that’s the entire point. Your device is supervised: the
            block doesn’t come off on an impulse. You stay free to stop, but
            through a deliberate request from your account, after a
            cooling-off delay — not in thirty seconds at two in the morning.`
      },
      {
        q: 'What’s this $100 thing?',
        a: `A bet we stand behind: on a device we locked ourselves, if you
            manage to strip the filter off by your own means, we send you a
            hundred dollars and refund you. It’s the logical consequence of
            supervision, which makes the profile immovable without going
            through your account.`
      },
      {
        q: 'Can you see what I look at?',
        a: `No. Blocking happens at DNS level, by whole categories. We don’t
            read your history, we don’t store your searches, and nobody
            receives a report. We provide the door, not a supervisor.`
      },
      {
        q: 'Which devices does it work on?',
        a: `iPhone, iPad, Mac and Windows. The reinforced lock — the one that
            doesn’t uninstall — goes through supervision of Apple devices,
            done once from a computer. On Mac and Windows, a small app you
            can’t quit does the same job.`
      },
      {
        q: 'What about private browsing? A VPN?',
        a: `Private browsing changes nothing: the filter works on the device,
            not in the browser. On a supervised iPhone, adding a workaround
            VPN is blocked by the profile itself.`
      },
      {
        q: 'Why does it cost money?',
        a: `Because free blockers live off your data, or abandon you at the
            first workaround. Here you’re the customer, not the product:
            maintained filtering infrastructure, no ads, nothing resold.`
      },
      {
        q: 'How long before it gets better?',
        a: `In the first few days your brain looks for the door and finds it
            locked. Most people describe real relief from the pressure two to
            four weeks in. The day counter does the rest.`
      },
      {
        q: 'Can I cancel?',
        a: `Yes, at any time, and you get a full refund within thirty days.
            Cancelling the subscription stops the billing; releasing a locked
            device goes through the cooling-off delay, like everything else.`
      }
    ]
  },

  relay: {
    cta: 'Lock it tonight',
    note: '30-day money-back · $100 if you beat it',
    afterProblem: 'Tonight can be the first night it simply isn’t there.',
    afterInside: 'Ten minutes today. No negotiation tonight.'
  },

  guidesTeaser: {
    kicker: 'Field notes',
    title: 'Device by device, what actually holds.',
    lead: `Screen Time, DNS profiles, hosts files, accountability apps: we
           tested how each one gets undone, and wrote it down.`,
    slugs: [
      'how-to-block-porn-on-iphone', 'how-to-block-porn-on-mac', 'how-to-block-porn-on-windows',
      'porn-blocker-that-blocks-vpn', 'how-to-quit-porn', 'covenant-eyes-alternative'
    ],
    read: 'Read',
    all: 'All the guides'
  },

  endCta: {
    clock: '02:47',
    title: <>Tonight,<br />it’s already locked.</>,
    lead: `Ten minutes this morning. Every device you own. And it does not come
           off at two in the morning.`,
    cta: 'Lock it today',
    hint: '$9.99/month · 30-day money-back · cancel anytime'
  },

  sticky: {
    text: <><b>$9.99/month</b> · 30-day money-back</>,
    cta: 'Get started'
  },

  // ==========================================================================
  //  RÉFÉRENCEMENT — partagée.
  // ==========================================================================
  seo: {
    home: {
      title: 'SELFCNTRL — Porn Blocker You Can’t Uninstall (iPhone, Mac, PC)',
      description: `Block porn on iPhone, iPad, Mac and PC with a supervised DNS filter you can’t remove at 2am. Covers private browsing and VPNs. $9.99/month, $100 if you beat it.`
    },
    register: {
      title: 'Create your space — $9.99/month',
      description: `One email address, ten minutes, and every device you own is
                    locked. $9.99/month or $49.99/year. Cancel in one click.`
    },
    guides: {
      title: 'Guides — how to block porn for good on every device',
      description: `Step-by-step guides to block porn on iPhone, iPad, Mac and
                    Windows, why Screen Time and free blockers fail, and how to
                    quit when willpower keeps losing.`,
      h1: 'Guides',
      lead: `Everything we know about shutting the door: device by device, and
             the mechanics behind it. Written in plain terms, no sermon.`,
      readMore: 'Read the guide',
      updated: d => `Updated ${d}`,
      minutes: n => `${n} min read`,
      crumbHome: 'Home',
      crumbGuides: 'Guides',
      next: 'Read next',
      ctaTitle: 'Shut the door tonight.',
      ctaBody: `Ten minutes, once. Every device you own, locked at DNS level —
                and it does not come off at two in the morning.`,
      ctaNote: '$9.99/month · $100 if you beat it'
    },
    notFound: {
      title: 'Page not found',
      description: 'This page does not exist.',
      h1: 'Nothing here.',
      p: 'The page you asked for does not exist, or has moved.',
      home: 'Back to home',
      guides: 'Read the guides'
    }
  },

  // Partagée.
  footer: {
    tagline: 'The porn blocker you can’t uninstall at 2am.',
    product: 'Product',
    company: 'Company',
    guides: 'Guides',
    contact: 'Contact',
    refund: 'Refunds',
    terms: 'Terms & conditions',
    privacy: 'Privacy',
    legal: 'Legal notice',
    login: 'Log in',
    pricing: 'Pricing',
    faq: 'FAQ',
    claim: 'First login',
    note: year => `© ${year} SELFCNTRL. No browsing history collected, ever.`
  },

  // Partagée (CookieBanner, masqué pour l'instant).
  cookies: {
    title: 'Cookies',
    body: `We use a measurement cookie to know which ads bring people here.
           Nothing about your browsing is read or stored.`,
    link: 'Privacy',
    accept: 'Accept',
    refuse: 'Refuse'
  },

  // Partagée (Slider).
  slider: {
    label: 'Scroll',
    item: (i, n) => `Item ${i} of ${n}`
  }
}

export default landing
